import React, { useState, useEffect } from 'react';

const ITEMS = [
  {
    id: 'ws',
    label: 'Server connected',
    readyText: 'Connected',
    pendingText: 'Connecting...',
    icon: '⚡',
  },
  {
    id: 'device',
    label: 'Phone system ready',
    readyText: 'Number registered',
    pendingText: 'Registering device...',
    icon: '📞',
  },
  {
    id: 'playbook',
    label: 'Playbook selected',
    readyText: 'Ready to coach',
    pendingText: 'Choose a playbook',
    icon: '📋',
  },
  {
    id: 'credits',
    label: 'Credits available',
    readyText: 'Billing OK',
    pendingText: 'Add credits to continue',
    icon: '💳',
  },
  {
    id: 'dial',
    label: 'Dial or answer a call',
    readyText: 'Call active!',
    pendingText: 'Enter a number below or answer an incoming call',
    icon: '🎯',
  },
];

export default function CallReadinessChecklist({
  wsConnected,
  deviceReady,
  playbookName,
  creditsOk,
  inCall,
}) {
  const [animatedChecks, setAnimatedChecks] = useState({});

  const checks = {
    ws: wsConnected,
    device: deviceReady,
    playbook: !!playbookName,
    credits: creditsOk,
    dial: inCall,
  };

  const completedCount = Object.values(checks).filter(Boolean).length;
  const progress = (completedCount / ITEMS.length) * 100;
  const allReady = completedCount === ITEMS.length;

  useEffect(() => {
    ITEMS.forEach((item) => {
      if (checks[item.id] && !animatedChecks[item.id]) {
        const timer = setTimeout(() => {
          setAnimatedChecks((prev) => ({ ...prev, [item.id]: true }));
        }, 80);
        return () => clearTimeout(timer);
      }
      if (!checks[item.id] && animatedChecks[item.id]) {
        setAnimatedChecks((prev) => ({ ...prev, [item.id]: false }));
      }
    });
  }, [wsConnected, deviceReady, playbookName, creditsOk, inCall]); // eslint-disable-line

  return (
    <div className="call-checklist">
      <div className="checklist-header">
        <div className="checklist-title">
          {allReady ? 'You\'re all set!' : 'Get ready for your call'}
        </div>
        <div className="checklist-subtitle">
          {allReady
            ? 'Your call is in progress — coaching will appear on the right.'
            : `${completedCount} of ${ITEMS.length} steps complete`}
        </div>
      </div>

      <div className="checklist-progress-track">
        <div
          className="checklist-progress-fill"
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="checklist-items">
        {ITEMS.map((item, idx) => {
          const checked = checks[item.id];
          const animated = animatedChecks[item.id];
          const isNext =
            !checked &&
            ITEMS.slice(0, idx).every((prev) => checks[prev.id]);

          return (
            <div
              key={item.id}
              className={`checklist-item ${checked ? 'checklist-item-done' : ''} ${isNext ? 'checklist-item-next' : ''} ${animated ? 'checklist-item-animated' : ''}`}
            >
              <div className={`checklist-check ${checked ? 'checklist-check-done' : ''}`}>
                {checked ? (
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M2.5 7.5L5.5 10.5L11.5 3.5"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <span className="checklist-step-num">{idx + 1}</span>
                )}
              </div>
              <div className="checklist-item-content">
                <div className="checklist-item-label">
                  <span className="checklist-item-icon">{item.icon}</span>
                  {item.label}
                </div>
                <div className="checklist-item-status">
                  {item.id === 'playbook' && checked
                    ? `${playbookName} active`
                    : checked ? item.readyText : item.pendingText}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
